import { useState, useEffect, useRef } from 'react';
import { Send, Sparkles, MessageSquare, AlertTriangle, Cpu, FileText, X, Zap, Bot, Copy, RefreshCw, ChevronDown } from 'lucide-react';
import { AgentReply } from './MessageBlocks';

// ─── 快捷提示词模板（含级联变量）─────────────────────────────────────────
const mockTemplates = [
  {
    id: 1, title: '需求文档编写',
    template: '请帮我编写一份关于 {{network}} / {{line}} / {{status}} / {{brand}} 的文档',
    variables: [
      { name: 'network', uiType: 'select', logicType: 'independent', options: ['城域网', '核心网'] },
      { name: 'line', uiType: 'select', logicType: 'cascading', dependsOn: 'network', cascadingMap: { '城域网': ['互联网专线', '数据专线'], '核心网': ['骨干链路', '传输链路'] } },
      { name: 'status', uiType: 'select', logicType: 'independent', options: ['开通', '变更', '维护'] },
      { name: 'brand', uiType: 'select', logicType: 'cascading', dependsOn: 'line', cascadingMap: { '互联网专线': ['华为', '中兴'], '数据专线': ['华为', 'H3C'], '骨干链路': ['华为', '诺基亚'], '传输链路': ['中兴', '烽火'] } },
    ],
  },
  {
    id: 2, title: '测试用例编写',
    template: '为 {{module}} 模块编写 {{testType}} 测试用例，优先级为 {{priority}}',
    variables: [
      { name: 'module', uiType: 'input', logicType: 'independent' },
      { name: 'testType', uiType: 'select', logicType: 'independent', options: ['单元测试', '集成测试', '接口测试', 'UI测试'] },
      { name: 'priority', uiType: 'select', logicType: 'independent', options: ['P0', 'P1', 'P2', 'P3'] },
    ],
  },
  {
    id: 3, title: '故障配置反解析',
    template: '分析 {{deviceType}} 设备的 {{faultType}} 故障，给出处理建议',
    variables: [
      { name: 'deviceType', uiType: 'select', logicType: 'independent', options: ['路由器', '交换机', '防火墙', '服务器'] },
      { name: 'faultType', uiType: 'select', logicType: 'cascading', dependsOn: 'deviceType', cascadingMap: { '路由器': ['路由震荡', '接口Down', 'CPU过高'], '交换机': ['端口环路', 'VLAN配置错误', 'STP故障'], '防火墙': ['策略冲突', '性能下降', '日志异常'], '服务器': ['宕机', '磁盘满', '内存不足'] } },
    ],
  },
];

// ─── 上下文感知（语义匹配方案）──────────────────────────────────────────────
// 平台对接入 Agent 仅掌握：名称 + 能力描述（接入时填写，非取 Dify 内部）。
// 推荐问题来源：① 接入时可选配的"推荐问题模板"(平台侧字段,含占位符) 优先；② 未配则由超级智能体基于"能力描述+实体"受约束生成。
const ONLINE_AGENTS = [
  { name: '告警智能诊断', desc: '网络告警根因分析、故障定位与处理建议',
    // 平台侧接入时可选配的推荐问题模板（非取 Agent 内部）
    recoTemplate: ['分析 {deviceId} 的 {alarmCode} 告警根因', '{alarmCode} 告警历史上的处理方案', '{deviceId} 该告警受影响的下游业务'] },
  { name: 'KPI异常分析', desc: '设备 KPI 时序趋势分析与异常检测',
    recoTemplate: null }, // 未配模板 → 走超级智能体基于能力描述+实体生成
  { name: '工单处理助手', desc: '工单处理方案生成、关联信息查询',
    recoTemplate: ['根据工单 {orderId} 生成处理方案', '{orderId} 关联设备有哪些活跃告警'] },
];

// 未配模板时，模拟超级智能体基于"能力描述 + 实体"受约束生成推荐问题
function genFromCapability(agent, entities) {
  const ent = Object.entries(entities).map(([k, v]) => `${k}=${v}`).join(' / ');
  return [
    `结合「${agent.desc}」，分析 ${entities.deviceId || ent} 的当前情况`,
    `${entities.deviceId || ''} ${entities.metric || ''} 是否存在异常`.trim(),
  ];
}

// 宿主页面：项目组集成时显式传入的页面语义 + 业务实体（SDK 不抓取页面文本；问题靠语义匹配 Agent 后填充）
const HOST_PAGES = [
  { key: 'alarm_detail', label: '告警详情页', icon: AlertTriangle, color: 'red',
    semantic: '告警 / 故障 / 根因', entities: { deviceId: 'NE-HB-Core-01', alarmCode: 'LINK_DOWN', level: 'critical' },
    matchAgent: '告警智能诊断', matchScore: 0.92 },
  { key: 'device_detail', label: '设备详情页', icon: Cpu, color: 'blue',
    semantic: '设备 / 性能 / KPI', entities: { deviceId: 'NE-SH-Edge-07', metric: 'CPU利用率', value: '78%' },
    matchAgent: 'KPI异常分析', matchScore: 0.88 },
  { key: 'workorder', label: '工单处理页', icon: FileText, color: 'green',
    semantic: '工单 / 故障处理', entities: { orderId: 'WO-2026-08821', priority: '紧急' },
    matchAgent: '工单处理助手', matchScore: 0.90 },
];

// 用实体填充常见问题预置的占位符；缺失实体的占位符保留原样（演示用）
function fillQuestion(tpl, entities) {
  return tpl.replace(/\{(\w+)\}/g, (_, k) => entities[k] ?? `{${k}}`);
}

// ─── 演示用 ReAct 回复（内容块）──────────────────────────────────────────
function buildReActReply() {
  return [
    { type: 'step', index: 1, action: '查询当前告警', status: 'done', latency: '0.8s', children: [
      { type: 'thinking', duration: 2, status: 'done', content: '解析用户意图，先获取相关网元的当前告警。' },
      { type: 'tool_call', tool: '当前告警批量查询', status: 'done', args: '{ "ne_id": "NE-HB-Core-01", "level": "critical" }' },
      { type: 'tool_result', resultType: 'json', content: '{\n  "count": 1,\n  "alarms": [{ "alarmId": "ALM-...0237", "code": "LINK_DOWN", "time": "09:14:22" }]\n}' },
    ] },
    { type: 'step', index: 2, action: '查询端口流量 KPI', status: 'done', latency: '1.2s', children: [
      { type: 'thinking', duration: 3, status: 'done', content: '结合端口流量 KPI 判断是物理断纤还是配置问题。' },
      { type: 'tool_call', tool: 'KPI指标时序查询', status: 'done', args: '{ "ne_id": "NE-HB-Core-01", "metrics": ["port_traffic"], "time_range": "近1小时" }' },
      { type: 'tool_result', resultType: 'log', content: '[09:13:50] port_traffic=2.4Gbps  OK\n[09:14:22] port_traffic=0Gbps     ALARM\n[09:14:30] link_state=DOWN        ERROR' },
    ] },
    { type: 'answer', content: '该告警为物理链路中断（LINK_DOWN）[1]。09:14:22 端口流量同步归零[2]，排除配置类问题，初判为光纤/物理层故障。\n\n建议：现场核查光功率，并检查对端设备状态。' },
    { type: 'citations', sources: [
      { doc: '当前告警批量查询', snippet: 'ALM-...0237 · LINK_DOWN · 09:14:22' },
      { doc: 'KPI指标时序查询', snippet: 'port_traffic 09:14:22 归零' },
    ] },
  ];
}

function buildFailReply() {
  return [
    { type: 'step', index: 1, action: '查询配置快照', status: 'failed', latency: '5.0s', children: [
      { type: 'thinking', duration: 2, status: 'done', content: '需要拉取设备配置快照做比对。' },
      { type: 'tool_call', tool: '设备配置快照查询', status: 'failed', args: '{ "ne_id": "NE-GZ-Access-12" }' },
      { type: 'error', scope: 'local', message: '工具调用超时（5s）：config-svc 暂不可达。按降级策略跳过该步骤。' },
    ] },
    { type: 'answer', content: '已基于当前告警信息给出初步判断。注意：因配置快照查询超时，本次未能比对配置变更，建议稍后重试该项以获得更完整结论。' },
  ];
}

// ─── 上下文感知（模拟宿主页面 + 悬浮窗内会话，不回填平台主视图）──────────────
function ContextAwareFloating({ onClose }) {
  const [page, setPage] = useState(HOST_PAGES[0]);
  const [winOpen, setWinOpen] = useState(true);
  const [matched, setMatched] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [winInput, setWinInput] = useState('');
  const bottomRef = useRef(null);

  useEffect(() => { setMatched(false); setMsgs([]); const t = setTimeout(() => setMatched(true), 700); return () => clearTimeout(t); }, [page]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs]);

  const agent = ONLINE_AGENTS.find(a => a.name === page.matchAgent);
  const contextSummary = `${page.label} · ${Object.entries(page.entities).map(([k, v]) => `${k}:${v}`).join(' / ')}`;
  const ask = (q) => { setMsgs(prev => [...prev, { role: 'user', text: q }, { role: 'agent', blocks: buildReActReply() }]); setWinInput(''); };
  const PageIcon = page.icon;

  return (
    <div className="fixed inset-0 z-50">
      {/* ① 模拟宿主业务页面（项目组的网管工作台，非平台视图） */}
      <div className="absolute inset-0 bg-gray-100 overflow-auto">
        <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between sticky top-0">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-slate-700 rounded flex items-center justify-center text-white text-xs font-bold">网</div>
            <span className="text-sm font-semibold text-gray-700">网管工作台（项目组业务系统）</span>
            <span className="text-xs px-1.5 py-0.5 bg-amber-50 text-amber-600 rounded border border-amber-200">宿主页面 · 非平台视图</span>
          </div>
          <button onClick={onClose} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"><X className="w-4 h-4" /> 退出演示</button>
        </div>

        <div className="max-w-3xl mx-auto p-8">
          <p className="text-xs text-gray-400 mb-3">切换模拟页面（项目组的不同业务页）：</p>
          <div className="flex gap-2 mb-6">
            {HOST_PAGES.map(pg => {
              const I = pg.icon;
              return <button key={pg.key} onClick={() => setPage(pg)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border ${page.key === pg.key ? 'bg-slate-700 text-white border-slate-700' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}><I className="w-3.5 h-3.5" /> {pg.label}</button>;
            })}
          </div>

          {/* 假业务内容卡片 */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-4"><PageIcon className="w-5 h-5 text-slate-600" /><h3 className="text-base font-bold text-gray-800">{page.label}</h3></div>
            <div className="grid grid-cols-2 gap-x-8 gap-y-3">
              {Object.entries(page.entities).map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-gray-100 pb-2">
                  <span className="text-sm text-gray-400">{k}</span><span className="text-sm font-medium text-gray-700">{v}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 p-2.5 bg-indigo-50 border border-indigo-100 rounded-lg text-xs text-indigo-600 leading-relaxed">
              项目组在本页集成 SDK：数据就绪时调用 <code className="px-1 bg-white/70 rounded">ECHO.setContext(&#123;pageType, entities&#125;)</code>，把上面这些<strong>结构化字段</strong>传给右下角悬浮 AI 入口（SDK 不抓取页面文本，传哪些由项目组指定）。<br/>
              <span className="text-indigo-400">单页核心代码量小，但需各页分别集成、按数据就绪时机调用、走业务排期——故 L1 定为可选试点。</span>
            </div>
          </div>
        </div>
      </div>

      {/* ② 悬浮球（收起态） */}
      {!winOpen && (
        <button onClick={() => setWinOpen(true)} className="fixed bottom-6 right-6 w-14 h-14 rounded-full bg-gradient-to-br from-indigo-600 to-blue-600 shadow-xl flex items-center justify-center hover:scale-105 transition-transform">
          <Sparkles className="w-6 h-6 text-white" />
        </button>
      )}

      {/* ③ 悬浮对话窗（会话全程在窗内，不进平台主视图） */}
      {winOpen && (
        <div className="fixed bottom-6 right-6 w-[380px] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden" style={{ height: 560 }}>
          <div className="px-4 py-3 bg-gradient-to-r from-indigo-600 to-blue-600 flex items-center justify-between">
            <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-yellow-300" /><span className="text-sm font-semibold text-white">AI 助手（嵌入宿主）</span></div>
            <button onClick={() => setWinOpen(false)} className="p-1 hover:bg-white/10 rounded text-white"><ChevronDown className="w-4 h-4" /></button>
          </div>

          {/* 上下文条 */}
          <div className="px-3 py-2 bg-gray-50 border-b border-gray-100 flex items-start gap-1.5">
            <Zap className="w-3.5 h-3.5 text-indigo-400 mt-0.5 flex-shrink-0" />
            <span className="text-xs text-gray-500">项目组传入上下文：{contextSummary}</span>
          </div>

          {/* 消息/推荐区 */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {msgs.length === 0 ? (
              !matched ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-400 text-sm"><Bot className="w-6 h-6 mb-1 animate-pulse text-indigo-400" />据页面上下文语义匹配 Agent…</div>
              ) : (
                <>
                  <div className="flex items-center gap-1.5 text-xs"><Bot className="w-3.5 h-3.5 text-indigo-500" /><span className="font-medium text-gray-700">匹配 Agent：{agent.name}</span><span className="px-1.5 py-0.5 bg-green-50 text-green-600 rounded">{(page.matchScore * 100).toFixed(0)}%</span></div>
                  <div className="text-xs text-gray-400">{agent.recoTemplate ? '该 Agent 配了推荐问题模板，用实体填充：' : '超级智能体据能力描述+实体生成推荐问题：'}</div>
                  <div className="space-y-1.5">
                    {(agent.recoTemplate ? agent.recoTemplate.map(t => fillQuestion(t, page.entities)) : genFromCapability(agent, page.entities)).map((q, i) => (
                      <button key={i} onClick={() => ask(q)} className="w-full text-left text-xs px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg border border-indigo-100">{q}</button>
                    ))}
                  </div>
                </>
              )
            ) : (
              msgs.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {m.role === 'user'
                    ? <div className="max-w-[80%] px-3 py-2 rounded-2xl text-xs bg-blue-600 text-white whitespace-pre-line">{m.text}</div>
                    : <div className="max-w-full w-full bg-gray-50 border border-gray-200 rounded-2xl px-3 py-2.5"><div className="flex items-center gap-1 text-xs text-indigo-500 mb-1.5 font-medium"><Bot className="w-3 h-3" /> 超级智能体</div><AgentReply blocks={m.blocks} /></div>}
                </div>
              ))
            )}
            <div ref={bottomRef} />
          </div>

          {/* 窗内输入框 */}
          <div className="p-2.5 border-t border-gray-100 flex items-center gap-2">
            <input value={winInput} onChange={e => setWinInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && winInput.trim() && ask(winInput)} placeholder="在此继续提问…" className="flex-1 px-3 py-1.5 text-xs bg-gray-50 rounded-lg outline-none" />
            <button onClick={() => winInput.trim() && ask(winInput)} disabled={!winInput.trim()} className={`p-2 rounded-lg ${winInput.trim() ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-400'}`}><Send className="w-4 h-4" /></button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── 主聊天界面 ────────────────────────────────────────────────────────────
export default function ChatInterface() {
  const [messages, setMessages] = useState([]);
  const [showContextPanel, setShowContextPanel] = useState(false);
  const [freeText, setFreeText] = useState('');
  // 快捷模板填充态
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [variableValues, setVariableValues] = useState({});
  const [showDropdown, setShowDropdown] = useState(null);
  const [editingVar, setEditingVar] = useState(null);
  const [editValue, setEditValue] = useState('');
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  useEffect(() => { setVariableValues({}); setShowDropdown(null); setEditingVar(null); }, [activeTemplate]);

  // 级联逻辑
  const getCascadingOptions = (v) => {
    if (v.uiType !== 'select') return [];
    if (v.logicType !== 'cascading' || !v.dependsOn) return v.options || [];
    const pv = variableValues[v.dependsOn];
    return pv ? (v.cascadingMap[pv] || []) : [];
  };
  const isVariableDisabled = (v) => v.logicType === 'cascading' && v.dependsOn && !variableValues[v.dependsOn];
  const handleVariableChange = (name, value) => {
    const v = activeTemplate?.variables.find(x => x.name === name);
    if (!v) return;
    if (v.uiType === 'select' && v.logicType === 'cascading' && v.dependsOn && !variableValues[v.dependsOn]) return;
    setVariableValues(prev => {
      const nv = { ...prev, [name]: value };
      if (value) activeTemplate.variables.filter(c => c.dependsOn === name).forEach(c => {
        delete nv[c.name];
        activeTemplate.variables.filter(g => g.dependsOn === c.name).forEach(g => delete nv[g.name]);
      });
      return nv;
    });
  };
  const isAllFilled = () => activeTemplate?.variables.every(v => (v.logicType === 'cascading' && v.dependsOn && !variableValues[v.dependsOn]) ? true : variableValues[v.name]);
  const getFinalPrompt = () => {
    if (!activeTemplate) return '';
    let r = activeTemplate.template;
    activeTemplate.variables.forEach(v => { if (variableValues[v.name]) r = r.replace(new RegExp(`\\{\\{${v.name}\\}\\}`, 'g'), variableValues[v.name]); });
    return r;
  };

  const sendMessage = (text, fail = null) => {
    if (!text.trim()) return;
    // 演示：文本含"失败/超时/异常"时走局部失败链路，否则正常 ReAct
    const useFail = fail === null ? /失败|超时|异常|不可达/.test(text) : fail;
    setMessages(prev => [...prev, { role: 'user', text }, { role: 'agent', agent: '超级智能体', blocks: useFail ? buildFailReply() : buildReActReply() }]);
    setActiveTemplate(null); setVariableValues({}); setFreeText('');
  };

  // 变量标签渲染
  const renderVariableTag = (variable) => {
    const value = variableValues[variable.name];
    const isDisabled = isVariableDisabled(variable);
    const options = getCascadingOptions(variable);
    if (variable.uiType === 'input' && editingVar === variable.name) {
      return <input key={variable.name} type="text" value={editValue} onChange={e => setEditValue(e.target.value)}
        onBlur={() => { if (editValue.trim()) handleVariableChange(editingVar, editValue.trim()); setEditingVar(null); setEditValue(''); }}
        onKeyDown={e => { if (e.key === 'Enter') { if (editValue.trim()) handleVariableChange(editingVar, editValue.trim()); setEditingVar(null); setEditValue(''); } else if (e.key === 'Escape') { setEditingVar(null); setEditValue(''); } }}
        className="px-2 py-1 text-sm font-medium bg-white border-b-2 border-blue-500 outline-none min-w-[80px]" placeholder={'输入' + variable.name} autoFocus />;
    }
    const tagStyle = isDisabled ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-60' : value ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-600 border border-blue-200';
    return (
      <span key={variable.name} onClick={() => { if (isDisabled) return; if (variable.uiType === 'input') { setEditValue(value || ''); setEditingVar(variable.name); } else setShowDropdown(showDropdown === variable.name ? null : variable.name); }}
        className={`px-3 py-1.5 text-sm font-medium rounded-lg cursor-pointer transition-all min-w-[90px] text-center inline-block ${tagStyle}`}>
        {value || `{{${variable.name}}}`}
        {!isDisabled && <span className="ml-1 text-xs opacity-70">{variable.uiType === 'input' ? '✎' : '▼'}</span>}
        {showDropdown === variable.name && !isDisabled && variable.uiType === 'select' && (
          <div className="absolute top-full left-0 mt-1 min-w-[160px] bg-white rounded-lg shadow-lg border border-gray-200 z-10">
            <div className="p-1">
              <button onClick={() => { handleVariableChange(variable.name, ''); setShowDropdown(null); }} className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 rounded ${!value ? 'bg-blue-50 text-blue-600' : 'text-gray-600'}`}>{'{{' + variable.name + '}}'}</button>
              {options.map((o, i) => <button key={i} onClick={() => { handleVariableChange(variable.name, o); setShowDropdown(null); }} className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 rounded ${value === o ? 'bg-blue-50 text-blue-600' : 'text-gray-600'}`}>{o}</button>)}
            </div>
          </div>
        )}
      </span>
    );
  };

  const renderTemplateInput = () => {
    if (!activeTemplate) {
      return <div className="flex items-center gap-2 text-gray-400 text-sm px-1"><Sparkles className="w-4 h-4" /><span>选择上方快捷模板填充变量，或点右上「上下文感知」</span></div>;
    }
    const parts = activeTemplate.template.split(/(\{\{\w+\}\})/g);
    return (
      <div className="flex flex-wrap items-center gap-2">
        {parts.map((part, i) => {
          const m = part.match(/\{\{(\w+)\}\}/);
          if (m) { const v = activeTemplate.variables.find(x => x.name === m[1]); return v ? <span key={i} className="relative">{renderVariableTag(v)}</span> : <span key={i} className="text-gray-700 text-sm">{part}</span>; }
          return <span key={i} className="text-gray-700 text-sm">{part}</span>;
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex flex-col">
      <header className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center"><MessageSquare className="w-5 h-5 text-white" /></div>
            <div><h1 className="text-lg font-semibold text-gray-800">智能助手</h1><p className="text-xs text-gray-500">超级智能体 · 过程可视化（内容块统一渲染）</p></div>
          </div>
          <button onClick={() => setShowContextPanel(true)} title="模拟宿主业务页面里的悬浮 AI 入口" className="flex items-center gap-1.5 px-4 py-2 mr-56 rounded-full text-sm font-medium transition-all border bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100">
            <Zap className="w-4 h-4" /> 上下文感知（宿主页面演示）
          </button>
        </div>
      </header>

      <main className="flex-1 flex flex-col max-w-3xl mx-auto w-full relative">
        <div className="flex-1 p-6 space-y-4 overflow-y-auto">
          {messages.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center max-w-md">
                <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg"><Sparkles className="w-12 h-12 text-white" /></div>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">您好，我是智能助手</h2>
                <p className="text-gray-500 mb-1">直接输入问题，或选下方快捷模板填充变量；回复以「内容块」统一渲染。</p>
                <p className="text-gray-400 text-sm">点击右上「上下文感知」从业务页面发起。（输入含"失败/超时"可演示局部失败链路）</p>
              </div>
            </div>
          ) : (
            messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role === 'user' ? (
                  <div className="max-w-lg px-4 py-2.5 rounded-2xl text-sm leading-relaxed bg-blue-600 text-white whitespace-pre-line">{msg.text}</div>
                ) : (
                  <div className="max-w-2xl w-full bg-white border border-gray-200 rounded-2xl px-4 py-3">
                    <div className="flex items-center gap-1 text-xs text-indigo-500 mb-2 font-medium"><Bot className="w-3.5 h-3.5" /> {msg.agent}</div>
                    <AgentReply blocks={msg.blocks} />
                    <div className="flex items-center gap-3 mt-3 pt-2 border-t border-gray-100 text-xs text-gray-400">
                      <button className="flex items-center gap-1 hover:text-gray-600"><Copy className="w-3 h-3" /> 复制回答</button>
                      <button className="flex items-center gap-1 hover:text-gray-600"><RefreshCw className="w-3 h-3" /> 重新生成</button>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
          <div ref={bottomRef} />
        </div>

        {showContextPanel && <ContextAwareFloating onClose={() => setShowContextPanel(false)} />}

        <div className="bg-white border-t border-gray-200 px-6 py-4">
          {/* 快捷提示词模板 */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="text-xs text-gray-400">快捷模板：</span>
            {mockTemplates.map(t => (
              <button key={t.id} onClick={() => setActiveTemplate(activeTemplate?.id === t.id ? null : t)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${activeTemplate?.id === t.id ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>{t.title}</button>
            ))}
          </div>
          {/* 输入区：未选模板时自由输入，选了模板时变量填充 */}
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200">
            <div className="flex-1 min-h-[40px] flex items-center">
              {activeTemplate ? renderTemplateInput() : (
                <input
                  type="text"
                  value={freeText}
                  onChange={(e) => setFreeText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') sendMessage(freeText); }}
                  placeholder="输入您的问题，或选择上方快捷模板 / 右上上下文感知…"
                  className="w-full bg-transparent text-sm outline-none px-1"
                />
              )}
            </div>
            {activeTemplate ? (
              <button onClick={() => sendMessage(getFinalPrompt())} disabled={!isAllFilled()}
                className={`p-2.5 rounded-xl transition-all ${isAllFilled() ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
                <Send className="w-5 h-5" />
              </button>
            ) : (
              <button onClick={() => sendMessage(freeText)} disabled={!freeText.trim()}
                className={`p-2.5 rounded-xl transition-all ${freeText.trim() ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
                <Send className="w-5 h-5" />
              </button>
            )}
          </div>
          {activeTemplate && (
            <div className="mt-2 text-xs text-gray-500 font-mono bg-gray-50 rounded p-2 break-all">最终发送：{getFinalPrompt() || '等待填充变量…'}</div>
          )}
        </div>
      </main>
    </div>
  );
}
